import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  clamp,
  DEFAULT_TEAM_NAMES,
  FONT_LIMITS,
  GameQuestion,
  MAX_TEAMS,
  MIN_TEAMS,
} from "./logic";

interface GameConfig {
  temaId?: number;
  temaNome?: string;
  teamNames: string[];
  seconds: number;
  /** perguntas que cada equipe responde (o total da partida = isto × nº de equipes) */
  questionsPerTeam: number;
  fontQuestion: number;
  fontOption: number;
}

export interface QuizPool {
  easy: any[];
  medium: any[];
  dificult: any[];
}

interface GameSession {
  questions: GameQuestion[];
  /** perguntas do tema já carregadas (em memória) para recomeçar sem novo pedido */
  quiz: QuizPool;
  /** ids de todas as perguntas já usadas neste ciclo de partidas (para não repetir) */
  usedIds: number[];
  index: number;
  /** resultado de cada pergunta já respondida (true = acerto) */
  results: boolean[];
  /** alternativa escolhida na pergunta actual; -1 = tempo esgotado; null = por responder */
  selected: number | null;
  teamNames: string[];
  seconds: number;
}

interface GameStore {
  config: GameConfig;
  session: GameSession | null;
  setConfig: (patch: Partial<GameConfig>) => void;
  setTeamCount: (count: number) => void;
  startSession: (questions: GameQuestion[], quiz: QuizPool) => void;
  /** nova partida com as mesmas equipes, tempo e tema */
  restartSession: (questions: GameQuestion[], usedIds: number[]) => void;
  answer: (selected: number) => void;
  next: () => void;
  endSession: () => void;
}

const defaultConfig: GameConfig = {
  teamNames: DEFAULT_TEAM_NAMES.slice(0, MIN_TEAMS),
  seconds: 20,
  questionsPerTeam: 10,
  fontQuestion: 26,
  fontOption: 18,
};

const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      config: defaultConfig,
      session: null,

      setConfig: (patch) => set({ config: { ...get().config, ...patch } }),

      setTeamCount: (count) => {
        const target = clamp(count, MIN_TEAMS, MAX_TEAMS);
        const current = get().config.teamNames;
        const teamNames = Array.from(
          { length: target },
          (_, i) => current[i] ?? DEFAULT_TEAM_NAMES[i],
        );
        set({ config: { ...get().config, teamNames } });
      },

      startSession: (questions, quiz) => {
        const { teamNames, seconds } = get().config;
        set({
          session: {
            questions,
            quiz,
            usedIds: questions.map((q) => q.id),
            index: 0,
            results: [],
            selected: null,
            teamNames: teamNames.map(
              (n, i) => n.trim() || DEFAULT_TEAM_NAMES[i],
            ),
            seconds,
          },
        });
      },

      restartSession: (questions, usedIds) => {
        const { session } = get();
        if (!session) return;
        set({
          session: { ...session, questions, usedIds, index: 0, results: [], selected: null },
        });
      },

      answer: (selected) => {
        const { session } = get();
        if (!session || session.selected !== null) return;
        const ok =
          selected >= 0 &&
          !!session.questions[session.index].options[selected]?.correct;
        set({
          session: {
            ...session,
            selected,
            results: [...session.results, ok],
          },
        });
      },

      next: () => {
        const { session } = get();
        if (!session || session.selected === null) return;
        set({ session: { ...session, index: session.index + 1, selected: null } });
      },

      endSession: () => set({ session: null }),
    }),
    {
      name: "quiz-config",
      // só as preferências persistem; a partida em curso não
      partialize: (state) => ({ config: state.config }),
      merge: (persisted: any, current) => {
        const config = { ...current.config, ...(persisted?.config ?? {}) };
        config.fontQuestion = clamp(config.fontQuestion, FONT_LIMITS.min, FONT_LIMITS.max);
        config.fontOption = clamp(config.fontOption, FONT_LIMITS.min, FONT_LIMITS.max);
        if (!Array.isArray(config.teamNames) || config.teamNames.length < MIN_TEAMS) {
          config.teamNames = defaultConfig.teamNames;
        }
        return { ...current, config };
      },
    },
  ),
);

export default useGameStore;
